import { useState } from 'react';
import {
  Clock, ChevronDown, ChevronRight, Ship, Package, Calendar,
  ArrowRight, Info,
} from 'lucide-react';
import { FeedbackButtons } from '../feedback/FeedbackButtons';
import type { Message as MessageType, EventTimeEvent } from '../../types';

interface EventTimePanelProps {
  message: MessageType;
}

/** Colour mapping for event categories. */
const categoryColors: Record<string, string> = {
  LOAD: 'bg-blue-100 text-blue-700 border-blue-200',
  DISCHARGE: 'bg-purple-100 text-purple-700 border-purple-200',
  GATE: 'bg-green-100 text-green-700 border-green-200',
  RAIL: 'bg-orange-100 text-orange-700 border-orange-200',
  VESSEL: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  TRANSSHIP: 'bg-pink-100 text-pink-700 border-pink-200',
  CUSTOMS: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  EMPTY: 'bg-gray-100 text-gray-600 border-gray-200',
};

function getCategoryColor(category: string): string {
  return categoryColors[category.toUpperCase()] || 'bg-gray-100 text-gray-600 border-gray-200';
}

/** Format an ISO date string for display. */
function formatEventDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/** Single event row in the timeline. */
function EventRow({ event, isLast }: { event: EventTimeEvent; isLast: boolean }) {
  return (
    <div className="flex gap-3">
      {/* Timeline dot + line */}
      <div className="flex flex-col items-center">
        <div className="w-2.5 h-2.5 rounded-full bg-teal-500 border-2 border-teal-200 flex-shrink-0 mt-1" />
        {!isLast && <div className="w-0.5 flex-1 bg-teal-200 min-h-[24px]" />}
      </div>

      {/* Event content */}
      <div className="flex-1 pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-800">
            {event.ff_event_type || event.event_type}
          </span>
          <span
            className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${getCategoryColor(event.event_category)}`}
          >
            {event.event_category}
          </span>
        </div>
        <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-500">
          <Calendar size={10} />
          {formatEventDate(event.event_date)}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders an event timeline response with:
 * - Header with booking/BL/container info
 * - Chronological timeline of events
 * - Context info (all BLs, all containers)
 */
export function EventTimePanel({ message }: EventTimePanelProps) {
  const [contextExpanded, setContextExpanded] = useState(false);
  const meta = message.eventTimeMeta;

  if (!meta) return null;

  const hasEvents = meta.events.length > 0;

  return (
    <div className="flex gap-2.5 animate-fade-in">
      {/* Avatar */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-teal-100">
        <Clock size={13} className="text-teal-600" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-gray-900">Event Timeline</span>
          <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded font-medium">
            {meta.client}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded font-medium">
            {meta.count} events
          </span>
        </div>

        {/* Booking / BL / Container badges */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded">
            <Ship size={11} className="text-blue-500" />
            <span className="text-blue-700 font-medium">Booking:</span>
            <span className="text-blue-800 font-mono">{meta.bookNo}</span>
          </div>
          <ArrowRight size={12} className="text-gray-300" />
          <div className="flex items-center gap-1 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded">
            <span className="text-indigo-700 font-medium">BL:</span>
            <span className="text-indigo-800 font-mono">{meta.blNo}</span>
          </div>
          <ArrowRight size={12} className="text-gray-300" />
          <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 border border-purple-200 rounded">
            <Package size={11} className="text-purple-500" />
            <span className="text-purple-700 font-medium">Container:</span>
            <span className="text-purple-800 font-mono">{meta.containerNo}</span>
          </div>
        </div>

        {/* Context info (all BLs, all containers) — collapsible */}
        {((meta.allBlNumbers && meta.allBlNumbers.length > 1) ||
          (meta.allContainerIds && meta.allContainerIds.length > 1)) && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setContextExpanded(!contextExpanded)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
            >
              {contextExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
              <Info size={12} className="text-gray-400" />
              <span className="text-xs font-semibold text-gray-600">
                Other BLs & Containers
              </span>
            </button>
            {contextExpanded && (
              <div className="px-3 pb-3 border-t border-gray-100 pt-2 space-y-2">
                {meta.allBlNumbers && meta.allBlNumbers.length > 1 && (
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      All BL Numbers ({meta.allBlNumbers.length})
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {meta.allBlNumbers.map((bl) => (
                        <span
                          key={bl}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                            bl === meta.blNo
                              ? 'bg-indigo-100 text-indigo-700 border border-indigo-300 font-semibold'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {bl}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {meta.allContainerIds && meta.allContainerIds.length > 1 && (
                  <div>
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                      All Containers ({meta.allContainerIds.length})
                    </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {meta.allContainerIds.map((cid) => (
                        <span
                          key={cid}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                            cid === meta.containerNo
                              ? 'bg-purple-100 text-purple-700 border border-purple-300 font-semibold'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {cid}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        {hasEvents ? (
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-3">
              <Clock size={12} className="text-teal-500" />
              <span className="text-xs font-semibold text-gray-700">
                Activity Timeline ({meta.count} events)
              </span>
              {meta.lastUpdated && (
                <span className="text-[10px] text-gray-400 ml-auto">
                  Updated: {new Date(meta.lastUpdated).toLocaleDateString()}
                </span>
              )}
            </div>
            <div>
              {meta.events.map((event, i) => (
                <EventRow
                  key={`${event.event_date}-${event.event_type}-${i}`}
                  event={event}
                  isLast={i === meta.events.length - 1}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-sm text-gray-500">
            No events found for this booking / BL / container combination.
          </div>
        )}

        {/* Feedback */}
        <div className="flex items-center gap-2 pt-1">
          <FeedbackButtons messageId={message.id} currentFeedback={message.feedbackGiven ?? null} />
        </div>
      </div>
    </div>
  );
}
